import { todayCivilDate } from '@sportninja/common/features/game/gameListView/venueLocalDateTime'
import { DateTime } from 'luxon'
import { useEffect, useRef, useState } from 'react'

/**
 * Anchor date for a calendar viewport, controlled OR uncontrolled.
 *
 * MySchedule mounts these components with no anchor at all and expects them to
 * page themselves, so the controlled props stay optional and the uncontrolled
 * path is unchanged. The Schedule views pass `sn_cal_date` down, which makes the
 * viewport a function of the URL — the only way Back, Forward and a shared link
 * can all land on the same month.
 */
export const useAnchorDate = (
  anchorDate?: string | null,
  onAnchorDateChange?: (date: string) => void
) => {
  const isControlled = anchorDate !== undefined

  //  The viewer's civil date, not UTC's, through the SHARED helper — the query
  //  window derives its own default from the same function, and the two drifting
  //  apart at a month boundary is what left the calendar empty.
  const [internal, setInternal] = useState<string>(todayCivilDate)

  const value =
    isControlled && anchorDate !== null && anchorDate !== ''
      ? anchorDate
      : internal

  const setValue = (next: string) => {
    if (!isControlled) {
      setInternal(next)
    }
    //  Fired only from user actions (prev / next / today), never from an effect
    //  reacting to props — otherwise the URL and the viewport chase each other.
    onAnchorDateChange?.(next)
  }

  return { anchor: DateTime.fromISO(value, { zone: 'UTC' }), setValue, value }
}

/**
 * Fires `onRangeChange` when the visible range changes — and ONLY then.
 *
 * The original effect listed the callback in its dependencies, so every parent
 * render with a fresh closure re-fired it, which set state upstream, which
 * re-rendered, which re-fired it. Keeping the callback in a ref and keying the
 * effect on the range string breaks that loop while still telling the parent
 * what to fetch.
 */
export const useRangeChangeNotifier = (
  start: string,
  endExclusive: string,
  onRangeChange?: (start: string, endInclusive: string) => void
) => {
  const callbackRef = useRef(onRangeChange)
  callbackRef.current = onRangeChange

  useEffect(() => {
    if (callbackRef.current === undefined) {
      return
    }
    const endInclusive = DateTime.fromISO(endExclusive, { zone: 'UTC' })
      .minus({ days: 1 })
      .toFormat('yyyy-MM-dd')
    callbackRef.current(start, endInclusive)
  }, [start, endExclusive])
}
